/**
 * Consent Outcomes — tenant-scoped consent history.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATES = ["", "PENDING", "GRANTED", "DENIED", "REVOKED", "EXPIRED"];
const COLORS = {
  PENDING: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-500",
  GRANTED: "bg-green-500/15 text-green-700 dark:text-green-500",
  DENIED: "bg-destructive/10 text-destructive",
  REVOKED: "bg-muted text-muted-foreground",
  EXPIRED: "bg-muted text-muted-foreground",
};

export default function ConsentOutcomes() {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (state) params.set("state", state);
    params.set("limit", "100");
    api.get(`/consent?${params}`)
      .then((r) => setRows(r.data?.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [state]);

  return (
    <div className="space-y-4" data-testid="institution-page-consents">
      <div>
        <h1 className="text-xl font-semibold">Consent Outcomes</h1>
        <p className="text-sm text-muted-foreground">
          Every consent this institution has requested — with the citizen's explicit decision and the audit_ref of the Trust Event (if any) that consumed it.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {STATES.map((s) => (
          <Button key={s || "all"} size="sm"
                  variant={state === s ? "default" : "outline"}
                  onClick={() => setState(s)}
                  data-testid={`consents-filter-${s || "all"}`}>
            {s || "All"}
          </Button>
        ))}
      </div>

      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="consents-table">
          <thead className="bg-muted/30 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Consent ID</th>
              <th className="text-left p-3">State</th>
              <th className="text-left p-3">Purpose</th>
              <th className="text-left p-3">Ledger token</th>
              <th className="text-left p-3">Requested</th>
              <th className="text-left p-3">Decided</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No consents match this filter.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.consent_id} className="border-t">
                <td className="p-3 font-mono text-[10px]">{r.consent_id}</td>
                <td className="p-3">
                  <span className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded ${COLORS[r.state] || "bg-muted"}`}>
                    {r.state}
                  </span>
                </td>
                <td className="p-3 text-xs">{r.purpose || "—"}</td>
                <td className="p-3 font-mono text-[10px]">{(r.vlk_id_ledger_token || r.vlk_id || "").slice(0, 22)}…</td>
                <td className="p-3 font-mono text-xs">{(r.requested_at || "").slice(0, 19)}</td>
                <td className="p-3 font-mono text-xs">{(r.decided_at || "").slice(0, 19) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
